import React, { useEffect, useMemo, useState } from "react";
import {
  ButtonWrapper,
  FormError,
  FormFieldWrapper,
  FormTitle,
  LidHeader,
  PageWrapper,
  StepDescription,
  StepHeader,
  StepMeta,
} from "./LidFormPage.styles";
import { FormData } from "./LidFormPage.types";
import DropdownWithObjects from "../../components/Dropdowns/DropdownWithObjects/DropdownWithObjects";
import Button from "../../components/Buttons/Button";
import SuccessModal from "../../components/Modals/SuccessModal/SuccessModal";
import { LeadData } from "../../services/leadService/leadService.types";
import {
  createLead,
  fetchCourses,
  fetchRoles,
  fetchTelegramApplicationLegalState,
  TelegramApplicationLegalDocument,
} from "../../services/leadService/leadService";
import { useViewportContext } from "../../context/ViewportContext";
import IOSCompatibleInput from "../../components/IOSCompatibleInput/IOSCompatibleInput";
import KeyboardDismisser from "../../components/KeyboardDismisser/KeyboardDismisser";
import {
  ConsentCheckbox,
  ConsentCopy,
  ConsentIntro,
  ConsentLink,
  ConsentList,
  ConsentRow,
  ConsentStatus,
} from "../RegistrationPage/RegistrationPage.styles";

import { logger } from "utils/logger";

type ApplicationStep = "details" | "consents";

const LidFormPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    surname: "",
    patronymic: "",
    phone: "",
    email: "",
    role: "",
    course: "",
  });
  const [step, setStep] = useState<ApplicationStep>("details");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [courses, setCourses] = useState<{ id: number; title: string }[]>([]);
  const [botId, setBotId] = useState<string | null>(null);
  const [userNameFromTelegram, setUserNameFromTelegram] = useState<string | null>(null);
  const [userTgId, setUserTgId] = useState<string | null>(null);
  const [defaultCourseId, setDefaultCourseId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLegalLoading, setIsLegalLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [legalDocuments, setLegalDocuments] = useState<TelegramApplicationLegalDocument[]>([]);
  const [acceptedDocumentIds, setAcceptedDocumentIds] = useState<Set<number>>(new Set());
  const [legalConfigurationComplete, setLegalConfigurationComplete] = useState(false);

  const { isKeyboardVisible, keyboardHeight } = useViewportContext();
  const bottomOffset = isKeyboardVisible ? keyboardHeight + 40 : 0;

  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.expand();
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const botIdFromTelegram = queryParams.get("bot_id");
    const userNameTelegram = queryParams.get("user_name");
    const courseIdFromUrl = queryParams.get("course_id");
    const userTGIdFromUrl = queryParams.get("user_id");

    if (botIdFromTelegram) {
      setBotId(botIdFromTelegram);
    } else {
      logger.error("Bot ID not found in URL.");
      setLoadError("Не удалось определить бота. Закройте форму и откройте её из Telegram ещё раз.");
      setIsLoading(false);
    }

    if (userTGIdFromUrl) {
      setUserTgId(userTGIdFromUrl);
    } else {
      setLegalConfigurationComplete(false);
      setIsLegalLoading(false);
      setLoadError("Не удалось определить Telegram-пользователя. Закройте форму и откройте её из бота ещё раз.");
    }

    if (courseIdFromUrl) {
      setDefaultCourseId(courseIdFromUrl);
    }

    if (userNameTelegram) {
      setUserNameFromTelegram(userNameTelegram);
    }
  }, []);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        if (!botId) {
          logger.error("Bot ID is not available. Cannot fetch data.");
          return;
        }

        setLoadError("");
        const [fetchedRoles, fetchedCourses] = await Promise.all([
          fetchRoles(botId),
          fetchCourses({ botId }),
        ]);

        setRoles(fetchedRoles);
        setCourses(fetchedCourses);

        if (!fetchedRoles.length || !fetchedCourses.length) {
          setLoadError(
            !fetchedCourses.length
              ? "У этого преподавателя сейчас нет доступных курсов для заявки."
              : "Не удалось получить список ролей для заявки.",
          );
        }

        if (defaultCourseId) {
          const defaultCourse = fetchedCourses.find(
            (course) => course.id.toString() === defaultCourseId,
          );
          if (defaultCourse) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              course: defaultCourse.id.toString(),
            }));
          } else if (fetchedCourses.length) {
            setLoadError("Выбранный курс недоступен для подачи заявки.");
          }
        }
      } catch (error) {
        logger.error("Ошибка при загрузке данных для выпадающих списков:", error);
        setLoadError("Не удалось загрузить курсы и роли. Закройте форму и попробуйте открыть её из Telegram ещё раз.");
      } finally {
        setIsLoading(false);
      }
    };

    if (botId) {
      void loadDropdownData();
    }
  }, [botId, defaultCourseId]);

  useEffect(() => {
    if (!botId || !userTgId) return;
    let active = true;
    setIsLegalLoading(true);
    fetchTelegramApplicationLegalState(botId, userTgId)
      .then((state) => {
        if (!active) return;
        setLegalDocuments(state.documents || []);
        setLegalConfigurationComplete(state.configuration_complete);
        setAcceptedDocumentIds(new Set());
        if (!state.configuration_complete || !(state.documents || []).length) {
          setLoadError("Обязательные документы для заявки временно настроены не полностью.");
        }
      })
      .catch((error) => {
        logger.error("Ошибка загрузки юридических документов заявки:", error);
        if (active) {
          setLegalConfigurationComplete(false);
          setLoadError("Не удалось загрузить обязательные согласия для заявки.");
        }
      })
      .finally(() => {
        if (active) setIsLegalLoading(false);
      });
    return () => {
      active = false;
    };
  }, [botId, userTgId]);

  const detailsComplete = useMemo(
    () => [
      formData.name,
      formData.surname,
      formData.phone,
      formData.email,
      formData.role,
      formData.course,
    ].every((value) => value.trim() !== ""),
    [formData],
  );

  const requiredLegalAccepted = useMemo(
    () => legalDocuments
      .filter((document) => document.required)
      .every((document) => acceptedDocumentIds.has(document.id)),
    [acceptedDocumentIds, legalDocuments],
  );

  const legalReady = !isLegalLoading && legalConfigurationComplete && legalDocuments.length > 0;
  const selectedCourseTitle = courses.find((course) => course.id.toString() === formData.course)?.title || "выбранный курс";

  const handleChange = (name: keyof FormData, value: string) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const validateDetails = () => {
    const requiredFields: Array<keyof FormData> = ["course", "role", "name", "surname", "phone", "email"];
    const nextErrors: Record<string, string> = {};
    requiredFields.forEach((field) => {
      if (!formData[field].trim()) nextErrors[field] = "Это поле обязательно";
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openConsents = () => {
    if (!validateDetails()) return;
    if (!legalReady) {
      setLoadError("Обязательные документы для заявки временно настроены не полностью.");
      return;
    }
    setErrors({});
    setStep("consents");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!detailsComplete || !validateDetails()) {
      setStep("details");
      return;
    }
    if (!legalReady) {
      setLoadError("Обязательные документы для заявки временно настроены не полностью.");
      return;
    }
    if (!requiredLegalAccepted) {
      setErrors({ accepted_document_ids: "Подтвердите все обязательные документы." });
      return;
    }
    if (!botId || !userTgId) {
      setLoadError("Не удалось определить данные Telegram для отправки заявки.");
      return;
    }

    setErrors({});
    const leadData: LeadData = {
      name: formData.name,
      surname: formData.surname,
      patronymic: formData.patronymic,
      accepted_document_ids: Array.from(acceptedDocumentIds),
      phone: formData.phone,
      email: formData.email,
      role: formData.role,
      course: formData.course ? Number(formData.course) : null,
      telegram: userNameFromTelegram,
      bot_id: botId,
      tg_id: userTgId,
      note: "",
    };

    try {
      await createLead(leadData);
      setShowSuccessModal(true);
      setTimeout(() => {
        if (window.Telegram?.WebApp?.close) {
          window.Telegram.WebApp.close();
        }
      }, 2000);
    } catch (error) {
      logger.error("Ошибка при создании лида:", error);
      setLoadError("Не удалось отправить заявку. Проверьте данные и попробуйте ещё раз.");
    }
  };

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (showSuccessModal) {
    return <SuccessModal message="Заявка успешно отправлена!" onClose={() => {}} />;
  }

  return (
    <>
      <KeyboardDismisser />
      <PageWrapper bottomOffset={bottomOffset}>
        <LidHeader>LISRENT</LidHeader>
        <StepHeader>
          <StepMeta>Шаг {step === "details" ? "1" : "2"} из 2</StepMeta>
          <FormTitle>{step === "details" ? "Заявка" : "Согласия"}</FormTitle>
          <StepDescription>
            {step === "details"
              ? "Заполните данные для заявки на курс. На следующем шаге нужно будет подтвердить обязательные документы."
              : `Подтвердите документы перед отправкой заявки на курс «${selectedCourseTitle}».`}
          </StepDescription>
        </StepHeader>

        {loadError && <FormError>{loadError}</FormError>}

        {step === "details" ? (
          <FormFieldWrapper>
            <DropdownWithObjects
              options={courses.map((course) => ({
                label: course.title,
                value: course.id.toString(),
              }))}
              placeholder="Курс"
              value={formData.course}
              onChange={(value) => handleChange("course", value)}
            />
            <DropdownWithObjects
              options={roles.map((role) => ({
                label: role.name,
                value: role.id.toString(),
              }))}
              placeholder="Роль"
              value={formData.role}
              onChange={(value) => handleChange("role", value)}
            />
            <IOSCompatibleInput
              type="text"
              placeholder="Имя"
              value={formData.name}
              onChange={(event) => handleChange("name", event.target.value)}
              hasError={Boolean(errors.name)}
            />
            <IOSCompatibleInput
              type="text"
              placeholder="Фамилия"
              value={formData.surname}
              onChange={(event) => handleChange("surname", event.target.value)}
              hasError={Boolean(errors.surname)}
            />
            <IOSCompatibleInput
              type="text"
              placeholder="Отчество"
              value={formData.patronymic}
              onChange={(event) => handleChange("patronymic", event.target.value)}
              hasError={false}
            />
            <IOSCompatibleInput
              type="text"
              placeholder="Номер телефона"
              value={formData.phone}
              onChange={(event) => handleChange("phone", event.target.value)}
              hasError={Boolean(errors.phone)}
            />
            <IOSCompatibleInput
              type="email"
              placeholder="Почта"
              value={formData.email}
              onChange={(event) => handleChange("email", event.target.value)}
              hasError={Boolean(errors.email)}
            />
          </FormFieldWrapper>
        ) : (
          <FormFieldWrapper>
            <ConsentIntro>
              Отметьте обязательные согласия. Текст каждого документа можно открыть по ссылке до подтверждения.
            </ConsentIntro>
            <ConsentList>
              {legalDocuments.map((document) => (
                <ConsentRow key={document.id}>
                  <ConsentCheckbox
                    type="checkbox"
                    checked={acceptedDocumentIds.has(document.id)}
                    onChange={(event) => {
                      setAcceptedDocumentIds((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(document.id);
                        else next.delete(document.id);
                        return next;
                      });
                      setErrors((current) => {
                        if (!current.accepted_document_ids) return current;
                        const next = { ...current };
                        delete next.accepted_document_ids;
                        return next;
                      });
                    }}
                  />
                  <ConsentCopy>
                    {document.url ? (
                      <ConsentLink href={document.url} target="_blank" rel="noreferrer">
                        {document.title}
                      </ConsentLink>
                    ) : document.title}
                    {document.required ? " *" : ""}
                  </ConsentCopy>
                </ConsentRow>
              ))}
            </ConsentList>
            {!legalConfigurationComplete && (
              <ConsentStatus $error>Обязательные документы для заявки настроены не полностью.</ConsentStatus>
            )}
            {errors.accepted_document_ids && (
              <ConsentStatus $error>{errors.accepted_document_ids}</ConsentStatus>
            )}
          </FormFieldWrapper>
        )}

        <ButtonWrapper>
          {step === "consents" && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setStep("details");
                setErrors({});
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              fullWidth
            >
              Назад
            </Button>
          )}
          <Button
            type="button"
            disabled={step === "details" ? !legalReady : !requiredLegalAccepted || !legalReady}
            onClick={step === "details" ? openConsents : handleSubmit}
            fullWidth
          >
            {step === "details" ? "Продолжить" : "Отправить заявку"}
          </Button>
        </ButtonWrapper>
      </PageWrapper>
    </>
  );
};

export default LidFormPage;
